import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import NodeValueRow from './NodeValueRow'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { selectedBorder } from './selectedBorder.js'

/**
 * Smooth Damp (ENG-1127) — eases a value from Current toward Target with
 * critically-damped spring behaviour: no overshoot, no ringing, and the same
 * trajectory at any frame rate. Unity's `Mathf.SmoothDamp` / Unreal's
 * `FMath::CriticallyDampedSmoothing`.
 *
 * It interpolates between TWO NUMBERS and publishes the result on `Value` —
 * Unreal's FInterpTo shape, where Current and Target are both values you can
 * wire. It names no variable: to drive something per frame, wire Progress into
 * a Set Variable fed from `Value`.
 *
 * An ASYNC node, drawn the way Unreal draws one — the clock badge in the corner
 * and three exec outputs, because "start this" and "it finished" are different
 * moments and a node with one output forces the author to pick:
 *
 *   Then      fires IMMEDIATELY, so the chain carries on while the value travels
 *   Finished  fires once, when the value settles
 *   Progress  fires every frame the value moves, with `Value` carrying it
 *
 * Firing it again while it runs retargets the same run (velocity kept) rather
 * than starting a second one; Stop cancels it where it stands, firing none of
 * the three. Target and Smooth time are re-read every frame, so a wired target
 * that moves is TRACKED — that is what this node is for, and why it is not a
 * lerp. Current is read ONCE, at start: it is where the run begins, and
 * re-reading it would restart the journey on every frame.
 *
 * The live readout is the runtime's own state (window.__SMOOTH_DAMP__, keyed by
 * node id). The node re-renders on the editor's 100ms debug poll, so it updates
 * as the value travels — a smoothing run is otherwise invisible in the editor,
 * and "nothing is happening" and "it finished instantly" look identical.
 */

export const SMOOTH_DAMP_ACCENT = '#40c8a0'

const HEADER      = '#062820'
const HEADER_SEL  = '#0a4038'
const BORDER_IDLE = '#0d3830'
const ROW_LINE    = '#0b2a24'
const LABEL       = '#5a8a80'
const EXEC        = '#ddd'   // exec pins are neutral squares, repo convention
const DATA_PIN    = '#60a0d0'
const execStyle   = { background: EXEC, width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }
const dataStyle   = { background: DATA_PIN, width: 10, height: 10, borderRadius: '50%', border: '2px solid #30608066' }
const FIELD_CLASS = nodeControlClass()

const fieldStyle = {
  width: '100%', boxSizing: 'border-box',
  background: '#020a0e', border: '1px solid #0d2830',
  color: SMOOTH_DAMP_ACCENT, padding: '4px 7px',
  borderRadius: 3, fontSize: 10,
  fontFamily: 'var(--m-mono)', outline: 'none',
}

/** A right-aligned exec OUTPUT row (Finished / Progress). */
function ExecOutRow({ id, label, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end',
      padding: '5px 12px 5px 16px', borderBottom: `1px solid ${ROW_LINE}`, position: 'relative',
    }}>
      {children}
      <span style={{ color: LABEL, fontSize: 10 }}>{label}</span>
      <Handle type="source" position={Position.Right} id={id} style={execStyle} />
    </div>
  )
}

export function SmoothDampNode({ id, data, selected }) {
  const fired = isFired(data)
  const sd = typeof window !== 'undefined' ? window.__SMOOTH_DAMP__ : null
  const run = sd?.runs?.().find(r => r.nodeId === id) || null
  // When nothing is travelling, report the last run instead of going blank. A
  // run that began already ON its target settles in one tick and moves nothing;
  // silence there is indistinguishable from a node that never fired, which is
  // exactly how this node got reported as broken when it was working.
  const last = !run ? (sd?.resultOf?.(id) ?? null) : null
  const set = (k, v) => data.onChange?.(k, v)
  const stop = e => e.stopPropagation()

  return (
    <div style={{
      background: selected ? '#04181a' : '#020e10',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(BORDER_IDLE) : BORDER_IDLE}`,
      borderRadius: 6,
      minWidth: 240,
      fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      {/* Header — execIn left, the IMMEDIATE exec out right, and the async clock
          badge in the corner (Unreal's marker for a node that keeps running
          after the chain has moved on). */}
      <div style={{
        background: selected ? HEADER_SEL : HEADER,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn" style={execStyle} />
        <span style={{ fontSize: 13 }}>〜</span>
        <span style={{ color: SMOOTH_DAMP_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Smooth Damp</span>
        <span
          title="Async — the chain continues immediately; Finished fires when the value settles"
          data-testid="smoothdamp-async-badge"
          style={{
            marginLeft: 'auto', width: 15, height: 15, borderRadius: '50%',
            border: `1px solid ${SMOOTH_DAMP_ACCENT}`, color: SMOOTH_DAMP_ACCENT,
            fontSize: 9, lineHeight: '13px', textAlign: 'center', flex: 'none',
          }}
        >🕐</span>
        <span style={{ color: LABEL, fontSize: 9 }}>Then</span>
        <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />
      </div>

      {/* Stop — cancels the run; fires none of the three outputs */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 12px 5px 16px', borderBottom: `1px solid ${ROW_LINE}`, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execStop" style={execStyle} />
        <span style={{ color: LABEL, fontSize: 10 }}>Stop</span>
      </div>

      <ExecOutRow id="execFinished" label="Finished" />

      <ExecOutRow id="execProgress" label="Progress">
        {run && (
          <span style={{ color: '#7ad29a', fontFamily: 'var(--m-mono)', fontSize: 10, marginRight: 'auto' }}
            data-testid="smoothdamp-live">
            {run.value.toFixed(3)} → {run.target ?? '—'}
          </span>
        )}
        {last && (
          <span style={{
            color: last.noop ? '#e0a050' : '#3f7a6e',
            fontFamily: 'var(--m-mono)', fontSize: 9, marginRight: 'auto',
          }} data-testid="smoothdamp-last">
            {last.noop
              ? `already at ${last.target} — nothing to interpolate`
              : `settled at ${Number(last.value).toFixed(3)}`}
          </span>
        )}
      </ExecOutRow>

      {/* Value — the same number Progress just wrote, as a data pin */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end',
        padding: '5px 12px 5px 16px', borderBottom: `1px solid ${ROW_LINE}`, position: 'relative',
      }}>
        <span style={{ color: DATA_PIN, fontSize: 10 }}>Value</span>
        <Handle type="source" position={Position.Right} id="out-value" style={dataStyle} />
      </div>

      {/* Current — where this run starts. A plain number, or wire one in
          (a Get Variable, a Get Camera FOV, anything float). */}
      <div style={{ padding: '8px 12px 4px', position: 'relative' }}>
        <div style={{ color: '#1a4038', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>CURRENT</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Handle type="target" position={Position.Left} id="in-current" style={dataStyle} />
          <input
            className={FIELD_CLASS}
            value={data.current ?? 0}
            onChange={e => set('current', e.target.value)}
            onMouseDown={stop}
            placeholder="0"
            data-testid="smoothdamp-current"
            style={{ ...fieldStyle, color: '#60d0b0' }}
          />
        </div>
      </div>

      {/* Target — free-form, so no slider: it can be any number, in any range */}
      <div style={{ padding: '4px 12px', position: 'relative' }}>
        <div style={{ color: '#1a4038', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>TARGET</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Handle type="target" position={Position.Left} id="in-target" style={dataStyle} />
          <input
            className={FIELD_CLASS}
            value={data.target ?? 0}
            onChange={e => set('target', e.target.value)}
            onMouseDown={stop}
            placeholder="1"
            data-testid="smoothdamp-target"
            style={{ ...fieldStyle, color: '#60d0b0' }}
          />
        </div>
      </div>

      <div style={{ padding: '2px 12px 10px' }}>
        <NodeValueRow
          label="Smooth time"
          value={Number(data.smoothTime ?? 0.3)}
          min={0.01} max={2} step={0.01}
          pinId="in-smoothTime"
          accent={SMOOTH_DAMP_ACCENT}
          testId="smoothdamp-smoothtime"
          onChange={v => set('smoothTime', v)}
        />
        <NodeValueRow
          label="Max speed"
          value={Number(data.maxSpeed ?? 0)}
          min={0} max={20} step={0.1}
          accent={SMOOTH_DAMP_ACCENT}
          testId="smoothdamp-maxspeed"
          onChange={v => set('maxSpeed', v)}
        />
        <div style={{ color: '#2a5048', fontSize: 9, marginTop: 4 }}>
          {Number(data.maxSpeed ?? 0) > 0 ? 'units / second cap' : 'max speed 0 = unlimited'}
        </div>
      </div>
    </div>
  )
}
